/**
 * Campos compartidos del formulario de dirección (MVP-02B §3).
 *
 * Perfil y Checkout renderizan exactamente esto. Lo único que cambia entre
 * contextos es el `variant`, que ajusta densidad, no comportamiento.
 *
 * Design system: componentes de `components/ui`, tokens semánticos DaisyUI
 * (funcionan en claro y oscuro sin ramas), label siempre encima del input y
 * error debajo, y cada recurso con sus estados de carga, vacío y error con
 * reintento accesible.
 */
import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from "@headlessui/react";
import {
    AlertTriangle,
    Check,
    ChevronDown,
    Hash,
    Home,
    Loader2,
    MapPin,
    Phone,
    RotateCw,
    UserRound,
} from "lucide-react";

import { SelectDropdown, TextInput, Toggle } from "../../components/ui/index.js";

const Campo = ({ id, label, requerido, error, ayuda, children }) => (
    <div className="form-control gap-1">
        <label className="label py-0" htmlFor={id}>
            <span className="label-text text-sm font-semibold">
                {label}
                {requerido && <span className="text-error"> *</span>}
            </span>
        </label>
        {children}
        {ayuda && !error && (
            <span className="label-text-alt text-base-content/60">{ayuda}</span>
        )}
        {error && (
            <span id={`${id}-error`} role="alert" className="label-text-alt text-error">
                {error}
            </span>
        )}
    </div>
);

/** Aviso de recurso caído con reintento alcanzable por teclado. */
const RecursoCaido = ({ mensaje, onReintentar }) => (
    <div
        role="alert"
        className="flex items-center justify-between gap-3 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2"
    >
        <span className="flex items-center gap-2 text-sm text-warning-content">
            <AlertTriangle size={15} className="shrink-0 text-warning" />
            {mensaje}
        </span>
        <button type="button" onClick={onReintentar} className="btn btn-ghost btn-xs gap-1">
            <RotateCw size={13} />
            Reintentar
        </button>
    </div>
);

const TODAS_LAS_SECCIONES = ["contact", "address", "location"];

const DireccionFormFields = ({
    form, variant = "profile", disabled = false,
    sections = TODAS_LAS_SECCIONES, showDefaultToggle = true,
}) => {
    const {
        value,
        errors,
        setField,
        cpEstado,
        onCodigoPostalChange,
        onProvinciaChange,
        onMunicipioChange,
        elegirCoincidencia,
        municipioSeleccionado,
        geografia,
    } = form;

    const [municipioQuery, setMunicipioQuery] = useState("");
    const compacto = variant === "checkout";

    const opcionesProvincia = useMemo(
        () => geografia.provincias.data.map((p) => ({ value: p.id, label: p.nombre })),
        [geografia.provincias.data],
    );

    const municipiosFiltrados = useMemo(() => {
        const q = municipioQuery.trim().toLowerCase();
        const lista = q
            ? geografia.municipiosUnicos.filter((m) => m.nombre.toLowerCase().includes(q))
            : geografia.municipiosUnicos;
        return lista.slice(0, 50);
    }, [geografia.municipiosUnicos, municipioQuery]);

    const provinciasCaidas = geografia.provincias.state === "error";
    const provinciasVacias = geografia.provincias.state === "empty";
    const municipiosCargando = geografia.municipios.state === "loading";
    const municipiosCaidos = geografia.municipios.state === "error";

    return (
        <div className={`flex flex-col ${compacto ? "gap-5" : "gap-8"}`}>
            {/* Contacto */}
            {sections.includes("contact") && (
            <section className="flex flex-col gap-3">
                {!compacto && (
                    <h3 className="text-xs uppercase tracking-widest text-base-content/50 font-semibold">
                        Contacto
                    </h3>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Campo id="nombre_contacto" label="Nombre de contacto" requerido error={errors.nombre_contacto}>
                        <TextInput
                            id="nombre_contacto"
                            type="text"
                            autoComplete="name"
                            value={value.nombre_contacto}
                            disabled={disabled}
                            onChange={(e) => setField("nombre_contacto", e.target.value)}
                            variant={errors.nombre_contacto ? "error" : undefined}
                            aria-invalid={Boolean(errors.nombre_contacto)}
                            aria-describedby={errors.nombre_contacto ? "nombre_contacto-error" : undefined}
                            placeholder="Quién recibe el pedido"
                            leftIcon={<UserRound size={16} />}
                        />
                    </Campo>
                    <Campo id="telefono" label="Teléfono móvil" requerido error={errors.telefono}>
                        <TextInput
                            id="telefono"
                            type="tel"
                            autoComplete="tel"
                            value={value.telefono}
                            disabled={disabled}
                            onChange={(e) => setField("telefono", e.target.value)}
                            variant={errors.telefono ? "error" : undefined}
                            aria-invalid={Boolean(errors.telefono)}
                            aria-describedby={errors.telefono ? "telefono-error" : undefined}
                            placeholder="+34 600 000 000"
                            leftIcon={<Phone size={16} />}
                        />
                    </Campo>
                </div>
            </section>
            )}

            {/* Dirección */}
            {sections.includes("address") && (
            <section className="flex flex-col gap-3">
                {!compacto && (
                    <h3 className="text-xs uppercase tracking-widest text-base-content/50 font-semibold">
                        Dirección
                    </h3>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Campo id="direccion" label="Calle y número" requerido error={errors.direccion}>
                        <TextInput
                            id="direccion"
                            type="text"
                            autoComplete="address-line1"
                            value={value.direccion}
                            disabled={disabled}
                            onChange={(e) => setField("direccion", e.target.value)}
                            variant={errors.direccion ? "error" : undefined}
                            aria-invalid={Boolean(errors.direccion)}
                            aria-describedby={errors.direccion ? "direccion-error" : undefined}
                            placeholder="Calle, número"
                            leftIcon={<Home size={16} />}
                        />
                    </Campo>
                    <Campo id="apartamento" label="Piso, puerta, etc." error={errors.apartamento}>
                        <TextInput
                            id="apartamento"
                            type="text"
                            autoComplete="address-line2"
                            value={value.apartamento ?? ""}
                            disabled={disabled}
                            onChange={(e) => setField("apartamento", e.target.value)}
                            placeholder="Apartamento, suite, unidad (opcional)"
                        />
                    </Campo>
                </div>
            </section>
            )}

            {/* Localización */}
            {sections.includes("location") && (
            <section className="flex flex-col gap-3">
                {!compacto && (
                    <h3 className="text-xs uppercase tracking-widest text-base-content/50 font-semibold">
                        Localización
                    </h3>
                )}

                {provinciasCaidas && (
                    <RecursoCaido
                        mensaje="No se pudo cargar la lista de provincias."
                        onReintentar={geografia.recargarProvincias}
                    />
                )}
                {provinciasVacias && !provinciasCaidas && (
                    <div role="alert" className="rounded-lg border border-error/40 bg-error/10 px-3 py-2 text-sm">
                        No hay datos geográficos disponibles. No es posible completar una dirección
                        ahora mismo; avisa al equipo para que revise la configuración del servidor.
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Campo
                        id="codigo_postal"
                        label="Código postal"
                        requerido
                        error={errors.codigo_postal}
                        ayuda="Rellenamos provincia y municipio por ti"
                    >
                        <TextInput
                            id="codigo_postal"
                            type="text"
                            inputMode="numeric"
                            autoComplete="postal-code"
                            value={value.codigo_postal}
                            disabled={disabled}
                            onChange={(e) => onCodigoPostalChange(e.target.value)}
                            variant={errors.codigo_postal ? "error" : undefined}
                            aria-invalid={Boolean(errors.codigo_postal)}
                            aria-describedby={errors.codigo_postal ? "codigo_postal-error" : undefined}
                            placeholder="28001"
                            leftIcon={
                                cpEstado.kind === "searching" ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Hash size={16} />
                                )
                            }
                        />
                    </Campo>

                    <Campo id="provincia" label="Provincia" requerido error={errors.provincia}>
                        <SelectDropdown
                            className="w-full"
                            value={value.provincia}
                            onChange={onProvinciaChange}
                            placeholder={
                                geografia.provincias.state === "loading" ? "Cargando…" : "Selecciona…"
                            }
                            disabled={disabled || geografia.provincias.state !== "ready"}
                            options={opcionesProvincia}
                        />
                    </Campo>

                    <Campo id="municipio" label="Municipio" requerido error={errors.municipio}>
                        <Combobox
                            value={municipioSeleccionado}
                            onChange={onMunicipioChange}
                            by={(a, b) => Number(a?.id) === Number(b?.id)}
                            disabled={disabled || !value.provincia || municipiosCargando}
                        >
                            <div className="relative">
                                <ComboboxInput
                                    id="municipio"
                                    className={`input input-bordered w-full pr-10 ${
                                        errors.municipio ? "input-error" : ""
                                    } ${!value.provincia ? "opacity-50" : ""}`}
                                    displayValue={(m) => m?.nombre ?? ""}
                                    onChange={(e) => setMunicipioQuery(e.target.value)}
                                    aria-invalid={Boolean(errors.municipio)}
                                    aria-describedby={errors.municipio ? "municipio-error" : undefined}
                                    placeholder={
                                        !value.provincia
                                            ? "Elige provincia primero"
                                            : municipiosCargando
                                              ? "Cargando municipios…"
                                              : "Busca tu municipio…"
                                    }
                                />
                                <ComboboxButton className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50">
                                    {municipiosCargando ? (
                                        <Loader2 size={16} className="animate-spin" />
                                    ) : (
                                        <ChevronDown size={16} />
                                    )}
                                </ComboboxButton>
                                <ComboboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-base-100 border border-base-300 shadow-xl p-1.5 flex flex-col gap-0.5">
                                    {municipiosFiltrados.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-base-content/50">
                                            Sin resultados
                                        </div>
                                    ) : (
                                        municipiosFiltrados.map((m) => (
                                            <ComboboxOption
                                                key={m.id}
                                                value={m}
                                                className={({ focus, selected }) =>
                                                    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors duration-100 ${
                                                        selected
                                                            ? "bg-primary/10 text-primary font-semibold"
                                                            : focus
                                                              ? "bg-base-200 text-base-content"
                                                              : "text-base-content"
                                                    }`
                                                }
                                            >
                                                {({ selected }) => (
                                                    <>
                                                        <span className="flex-1 truncate">{m.nombre}</span>
                                                        {selected && (
                                                            <Check size={13} className="shrink-0 text-primary" />
                                                        )}
                                                    </>
                                                )}
                                            </ComboboxOption>
                                        ))
                                    )}
                                </ComboboxOptions>
                            </div>
                        </Combobox>
                    </Campo>
                </div>

                {municipiosCaidos && (
                    <RecursoCaido
                        mensaje="No se pudo cargar la lista de municipios."
                        onReintentar={geografia.recargarMunicipios}
                    />
                )}

                {/* Resultado del código postal */}
                {cpEstado.kind === "multiple" && (
                    <div className="flex flex-col gap-2 rounded-lg border border-info/40 bg-info/10 px-3 py-3">
                        <p className="text-sm font-medium">
                            El código postal {cpEstado.codigoPostal} corresponde a varios municipios.
                            Elige el tuyo:
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {cpEstado.coincidencias.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => elegirCoincidencia(c)}
                                    className="btn btn-sm btn-outline"
                                >
                                    {c.nombre}
                                    <span className="opacity-60">({c.provincia_nombre})</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {cpEstado.kind === "ok" && municipioSeleccionado && (
                    <p className="flex items-center gap-2 text-sm text-success">
                        <Check size={14} className="shrink-0" />
                        {municipioSeleccionado.nombre}
                        {value.provincia && geografia.provincias.data.length > 0 && (
                            <span className="text-base-content/60">
                                (
                                {geografia.provincias.data.find(
                                    (p) => Number(p.id) === Number(value.provincia),
                                )?.nombre}
                                )
                            </span>
                        )}
                    </p>
                )}

                {cpEstado.kind === "none" && (
                    <p className="flex items-center gap-2 text-sm text-warning">
                        <AlertTriangle size={14} className="shrink-0" />
                        No encontramos ese código postal. Selecciona provincia y municipio a mano.
                    </p>
                )}

                {cpEstado.kind === "error" && (
                    <p className="flex items-center gap-2 text-sm text-warning">
                        <AlertTriangle size={14} className="shrink-0" />
                        No se pudo comprobar el código postal. Puedes seguir y elegir provincia y
                        municipio a mano.
                    </p>
                )}

                {cpEstado.kind === "varios_cp" && (
                    <p className="flex items-center gap-2 text-sm text-warning">
                        <AlertTriangle size={14} className="shrink-0" />
                        {cpEstado.municipio} tiene varios códigos postales. Comprueba que el tuyo es
                        correcto.
                    </p>
                )}
            </section>
            )}

            {showDefaultToggle && (
            <Toggle
                checked={Boolean(value.predeterminado)}
                onChange={(v) => setField("predeterminado", v)}
                label="Usar como dirección de envío predeterminada"
                color="primary"
                disabled={disabled}
            />
            )}

            {errors.general && (
                <p role="alert" className="flex items-center gap-2 text-sm text-error">
                    <MapPin size={14} className="shrink-0" />
                    {errors.general}
                </p>
            )}
        </div>
    );
};

DireccionFormFields.propTypes = {
    // Objeto devuelto por `useDireccionForm`: valor, errores, estado del código
    // postal y acceso a la geografía. Se valida como un todo porque es el
    // contrato del hook, no una bolsa de props sueltas.
    form: PropTypes.shape({
        value: PropTypes.object.isRequired,
        errors: PropTypes.object.isRequired,
        setField: PropTypes.func.isRequired,
        cpEstado: PropTypes.object.isRequired,
        onCodigoPostalChange: PropTypes.func.isRequired,
        onProvinciaChange: PropTypes.func.isRequired,
        onMunicipioChange: PropTypes.func.isRequired,
        elegirCoincidencia: PropTypes.func.isRequired,
        municipioSeleccionado: PropTypes.object,
        geografia: PropTypes.object.isRequired,
    }).isRequired,
    variant: PropTypes.oneOf(["profile", "checkout"]),
    disabled: PropTypes.bool,
    // Qué secciones renderizar. Por defecto las tres (comportamiento previo,
    // sin cambios para Perfil/Checkout). El admin de envíos solo necesita
    // "location" para reutilizar el selector de provincia/municipio/CP en la
    // dirección de empresa, que no tiene contacto de entrega ni predeterminada.
    sections: PropTypes.arrayOf(PropTypes.oneOf(["contact", "address", "location"])),
    showDefaultToggle: PropTypes.bool,
};

Campo.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    requerido: PropTypes.bool,
    error: PropTypes.string,
    ayuda: PropTypes.string,
    children: PropTypes.node,
};

RecursoCaido.propTypes = {
    mensaje: PropTypes.string.isRequired,
    onReintentar: PropTypes.func.isRequired,
};

export default DireccionFormFields;
